import { lazy, Suspense, useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState, Skeleton } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { QrCode, Scan, Package as PackageIcon, Camera } from "@phosphor-icons/react";

// Lazy-load QRCode library (only needed when the QR modal opens)
const QRCodeSVG = lazy(() => import("qrcode.react").then((m) => ({ default: m.QRCodeSVG })));
const CameraQRScanner = lazy(() => import("@/components/CameraQRScanner"));

export default function QRDispatch() {
  const [packages, setPackages] = useState([]);
  const [scans, setScans] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newPkg, setNewPkg] = useState(null);
  const [scanInput, setScanInput] = useState("");
  const [scanAction, setScanAction] = useState("dispatch");
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [showCamera, setShowCamera] = useState(false);

  const load = async () => {
    try {
      const [p, s, o] = await Promise.all([
        api.get("/dispatch/packages"),
        api.get("/dispatch/scans?limit=50"),
        api.get("/sales/orders"),
      ]);
      setPackages(p.data); setScans(s.data); setOrders(o.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const createPackage = async (order_id, awb, vendor) => {
    try {
      await api.post("/dispatch/packages", { order_id, awb, shipping_vendor_id: vendor });
      toast.success("Package created"); setNewPkg(null); load();
    } catch { toast.error("Failed"); }
  };

  const scan = async () => {
    try {
      const { data } = await api.post("/dispatch/scan", { qr_data: scanInput, package_id: scanInput, action: scanAction, device: "webcam" });
      if (data.ok) toast.success(`OK — order now ${data.order_status}`);
      else toast.error(`Blocked: ${data.blocked?.join(", ")}`);
      setScanInput(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed"); }
  };

  return (
    <div data-testid="dispatch-page" className="page-fade">
      <PageHeader eyebrow="Module" title="QR dispatch" description="Scan-to-dispatch with idempotent verification. Prevents dispatch on unmet preconditions." tint="teal" icon={QrCode}/>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="card-elevated" data-testid="qr-scanner-panel">
          <div className="label-eyebrow">Scanner</div>
          <div className="display-3 mt-1">Scan QR / Package ID</div>
          <div className="flex gap-2 mt-5 flex-wrap">
            {[["dispatch","Handoff to packing"],["courier_handover","Courier handover"]].map(([v,l])=>(
              <button key={v} onClick={()=>setScanAction(v)} className={`pill-toggle ${scanAction===v?"is-active":""}`} data-testid={`scan-action-${v}`}>{l}</button>
            ))}
          </div>
          <input value={scanInput} onChange={(e)=>setScanInput(e.target.value)} placeholder="Paste QR data or package ID (AE-PKG-XXXX)"
                 className="input-soft mt-5 font-mono" data-testid="scan-input"
                 onKeyDown={(e)=>e.key==="Enter"&&scan()}/>
          <div className="flex gap-2 mt-3">
            <button onClick={()=>setShowCamera(true)} className="btn-ghost flex-1" data-testid="scan-open-camera"><Camera size={14}/> Live camera scan</button>
            <button onClick={scan} disabled={!scanInput} className="btn-primary flex-1" data-testid="scan-submit"><Scan size={14}/> Scan</button>
          </div>
          <p className="text-xs text-muted-foreground mt-4 leading-relaxed">Use the live scanner on any device with a camera, or paste a QR/package ID for manual entry.</p>
        </div>

        <div className="card-elevated lg:col-span-2" data-testid="scan-log">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-eyebrow">Recent scans</div>
              <div className="display-3 mt-1">Audit-visible log</div>
            </div>
          </div>
          <div className="card-soft p-0 overflow-x-auto">
            <table className="table-modern">
              <thead><tr><th>When</th><th>Type</th><th>Package</th><th>By</th><th>Result</th></tr></thead>
              <tbody>
                {scans.map((s) => (
                  <tr key={s._id} data-testid={`scan-row-${s._id}`}>
                    <td className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(s.created_at)}</td>
                    <td className="text-sm capitalize">{(s.scan_type||"").replace(/_/g," ")}</td>
                    <td className="font-mono text-xs">{s.package_id || "—"}</td>
                    <td className="text-sm">{s.scanned_by}</td>
                    <td><StatusChip value={s.result}/> {s.reason && <span className="text-xs text-destructive ml-2">{s.reason}</span>}</td>
                  </tr>
                ))}
                {!scans.length && <tr><td colSpan={5}><EmptyState icon={Scan} title="No scans yet" description="Scan a QR code to begin the dispatch flow." /></td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow">Packages</div>
            <div className="display-3 mt-1">Ready to dispatch</div>
          </div>
          <button className="btn-primary" onClick={()=>setNewPkg({ order_id: "", awb: "", shipping_vendor_id: "" })} data-testid="new-package-btn"><PackageIcon size={14}/> New package</button>
        </div>
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>Package</th><th>Order</th><th>AWB</th><th>Status</th><th>Created</th><th></th></tr></thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p._id} data-testid={`pkg-row-${p.package_id}`}>
                  <td className="font-mono text-xs">{p.package_id}</td>
                  <td className="font-mono text-xs">{p.order_number}</td>
                  <td className="font-mono text-xs">{p.awb || "—"}</td>
                  <td><StatusChip value={p.status}/></td>
                  <td className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</td>
                  <td className="text-right">
                    <button onClick={()=>setSelectedPkg(p)} className="btn-ghost btn-sm" data-testid={`pkg-view-${p.package_id}`}><QrCode size={12}/> QR</button>
                    <RecordActions entity="packages" record={p} frozen={["package_id","order_number"]} onChanged={load} />
                  </td>
                </tr>
              ))}
              {!packages.length && <tr><td colSpan={6}><EmptyState icon={PackageIcon} title="No packages yet" description="Create a package to generate a QR code for dispatch." /></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {newPkg && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setNewPkg(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-md" data-testid="new-package-form">
            <div className="label-eyebrow">New package</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <label className="block"><span className="label-eyebrow">Order</span>
              <select value={newPkg.order_id} onChange={(e)=>setNewPkg({...newPkg, order_id:e.target.value})}
                      className="input-soft mt-2" data-testid="pkg-order-select">
                <option value="">— Select order —</option>
                {orders.map((o)=><option key={o._id} value={o._id}>{o.order_number} · {o.customer?.name}</option>)}
              </select>
            </label>
            <label className="block mt-5"><span className="label-eyebrow">AWB (tracking number)</span>
              <input value={newPkg.awb} onChange={(e)=>setNewPkg({...newPkg, awb:e.target.value})}
                     className="input-underline font-mono" data-testid="pkg-awb"/></label>
            <div className="flex gap-2 mt-8">
              <button onClick={()=>setNewPkg(null)} className="btn-ghost flex-1">Cancel</button>
              <button onClick={()=>createPackage(newPkg.order_id, newPkg.awb, newPkg.shipping_vendor_id)} disabled={!newPkg.order_id} className="btn-primary flex-1" data-testid="pkg-create-submit">Create package</button>
            </div>
          </div>
        </div>
      )}

      {selectedPkg && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setSelectedPkg(null)}>
          <div onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-8 w-full max-w-sm text-center" data-testid="pkg-qr-modal">
            <div className="label-eyebrow">Package</div>
            <div className="display-3 mt-1">{selectedPkg.package_id}</div>
            <div className="mt-6 flex justify-center bg-white p-4 border border-border rounded-xl">
              <Suspense fallback={<Skeleton className="w-[220px] h-[220px]" />}>
                <QRCodeSVG value={selectedPkg.qr_data} size={220} level="M"/>
              </Suspense>
            </div>
            <div className="mt-4 font-mono text-xs break-all text-muted-foreground">{selectedPkg.qr_data}</div>
            <button onClick={()=>{ navigator.clipboard?.writeText(selectedPkg.qr_data); toast.success("QR data copied — paste into scanner input"); }} className="btn-ghost w-full mt-5" data-testid="pkg-copy-qr">Copy QR for scan test</button>
          </div>
        </div>
      )}

      {showCamera && (
        <Suspense fallback={<div />}>
          <CameraQRScanner
            onScan={(text) => { setScanInput(text); setShowCamera(false); toast.success("QR captured — press Scan to verify"); }}
            onClose={() => setShowCamera(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
